import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import toast from "react-hot-toast";
import { useCart } from "../../context/CartContext";
import "./Checkout.css";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || process.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY;

const DELIVERY_OPTIONS = [
	{ id: "local", label: "Local Delivery", description: "Within 15 km", price: 12 },
	{ id: "extended", label: "Extended Delivery", description: "15 – 30 km", price: 18 },
	{ id: "extended-plus", label: "Extended+ Delivery", description: "30+ km", price: 25 },
];

const Checkout = () => {
	const { items, subtotal, clearCart } = useCart();
	const [isProcessing, setIsProcessing] = useState(false);
	const [step, setStep] = useState("information");

	const [formData, setFormData] = useState({
		email: "",
		firstName: "",
		lastName: "",
		address: "",
		city: "",
		state: "",
		zip: "",
		phone: "",
		deliveryMethod: "delivery",
		deliveryZone: "local",
	});

	const shippingCost = formData.deliveryMethod === "pickup"
		? 0
		: DELIVERY_OPTIONS.find((o) => o.id === formData.deliveryZone)?.price || 12;
	const total = subtotal + shippingCost;

	useEffect(() => {
		window.scrollTo({ top: 0 });
	}, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleContinueToPayment = (e) => {
		e.preventDefault();
		const { email, firstName, lastName } = formData;
		if (!email || !firstName || !lastName) {
			toast.error("Please fill in all required fields.");
			return;
		}
		if (formData.deliveryMethod === "delivery" && (!formData.address || !formData.city || !formData.zip)) {
			toast.error("Please fill in your delivery address.");
			return;
		}
		setStep("payment");
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const handlePlaceOrder = async () => {
		setIsProcessing(true);

		try {
			const effectiveTotal = formData.deliveryMethod === "pickup" ? subtotal : total;
			const effectiveShipping = formData.deliveryMethod === "pickup" ? 0 : shippingCost;

			const lineItems = items.map((item) => ({
				name: item.title,
				amount: Math.round(item.price * 100),
				quantity: item.quantity,
				image: item.images?.[0] || undefined,
			}));

			if (effectiveShipping > 0) {
				const zone = DELIVERY_OPTIONS.find((o) => o.id === formData.deliveryZone);
				lineItems.push({
					name: `Shipping — ${zone?.label || "Delivery"}`,
					amount: Math.round(effectiveShipping * 100),
					quantity: 1,
				});
			}

			const origin = window.location.origin;

			const response = await fetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
					"apikey": SUPABASE_ANON_KEY,
				},
				body: JSON.stringify({
					line_items: lineItems,
					success_url: `${origin}/order-confirmed?session_id={CHECKOUT_SESSION_ID}`,
					cancel_url: `${origin}/checkout`,
					customer_email: formData.email,
					metadata: {
						customer_name: `${formData.firstName} ${formData.lastName}`,
						delivery_method: formData.deliveryMethod,
						phone: formData.phone || "",
						address: formData.deliveryMethod === "delivery"
							? `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}`
							: "Studio Pickup",
					},
				}),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.error || "Failed to create checkout session");
			}

			if (data.url) {
				clearCart();
				window.location.href = data.url;
			} else {
				throw new Error("No checkout URL returned");
			}
		} catch (err) {
			console.error("Checkout error:", err);
			toast.error("Something went wrong. Please try again.");
			setIsProcessing(false);
		}
	};

	if (items.length === 0 && step !== "payment") {
		return (
			<>
				<Helmet>
					<title>Checkout - Aire Studio</title>
				</Helmet>
				<div className="checkout-empty">
					<h2>Your cart is empty</h2>
					<Link to="/collections" className="checkout-shop-link">Continue Shopping</Link>
				</div>
			</>
		);
	}

	return (
		<>
			<Helmet>
				<title>Checkout - Aire Studio</title>
			</Helmet>

			<div className="checkout-page">
				<div className="checkout-header">
					<Link to="/" className="checkout-logo">Aire Studio</Link>
				</div>

				<div className="checkout-layout">
					<motion.div
						className="checkout-form-side"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.4 }}
					>
						<nav className="checkout-breadcrumb">
							<Link to="/collections">Cart</Link>
							<span>/</span>
							<button
								className={step === "information" ? "active" : ""}
								onClick={() => setStep("information")}
							>
								Information
							</button>
							<span>/</span>
							<span className={step === "payment" ? "active" : ""}>Payment</span>
						</nav>

						{step === "information" ? (
							<form className="checkout-form" onSubmit={handleContinueToPayment}>
								<div className="checkout-section">
									<h3 className="checkout-section-title">Contact</h3>
									<input
										type="email"
										name="email"
										placeholder="Email address"
										value={formData.email}
										onChange={handleChange}
										className="checkout-input"
										required
									/>
									<input
										type="tel"
										name="phone"
										placeholder="Phone (optional)"
										value={formData.phone}
										onChange={handleChange}
										className="checkout-input"
									/>
								</div>

								<div className="checkout-section">
									<h3 className="checkout-section-title">Delivery Method</h3>
									<div className="checkout-delivery-options">
										{DELIVERY_OPTIONS.map((option) => (
											<label
												key={option.id}
												className={`checkout-radio ${formData.deliveryMethod === "delivery" && formData.deliveryZone === option.id ? "selected" : ""}`}
											>
												<input
													type="radio"
													name="deliveryZone"
													value={option.id}
													checked={formData.deliveryMethod === "delivery" && formData.deliveryZone === option.id}
													onChange={(e) => {
														setFormData((prev) => ({
															...prev,
															deliveryMethod: "delivery",
															deliveryZone: e.target.value,
														}));
													}}
												/>
												<span className="checkout-radio-dot" />
												<div className="checkout-radio-content">
													<div className="checkout-radio-top">
														<strong>{option.label}</strong>
														<span className="checkout-radio-price">${option.price.toFixed(2)}</span>
													</div>
													<p>{option.description}</p>
												</div>
											</label>
										))}
										<label className={`checkout-radio ${formData.deliveryMethod === "pickup" ? "selected" : ""}`}>
											<input
												type="radio"
												name="deliveryMethod"
												value="pickup"
												checked={formData.deliveryMethod === "pickup"}
												onChange={handleChange}
											/>
											<span className="checkout-radio-dot" />
											<div className="checkout-radio-content">
												<div className="checkout-radio-top">
													<strong>Studio Pickup</strong>
													<span className="checkout-radio-price">Free</span>
												</div>
												<p>Collect from our studio</p>
											</div>
										</label>
									</div>
								</div>

								{formData.deliveryMethod === "delivery" && (
									<div className="checkout-section">
										<h3 className="checkout-section-title">Shipping Address</h3>
										<div className="checkout-row">
											<input
												type="text"
												name="firstName"
												placeholder="First name"
												value={formData.firstName}
												onChange={handleChange}
												className="checkout-input"
												required
											/>
											<input
												type="text"
												name="lastName"
												placeholder="Last name"
												value={formData.lastName}
												onChange={handleChange}
												className="checkout-input"
												required
											/>
										</div>
										<input
											type="text"
											name="address"
											placeholder="Address"
											value={formData.address}
											onChange={handleChange}
											className="checkout-input"
											required
										/>
										<div className="checkout-row checkout-row-3">
											<input
												type="text"
												name="city"
												placeholder="City"
												value={formData.city}
												onChange={handleChange}
												className="checkout-input"
												required
											/>
											<input
												type="text"
												name="state"
												placeholder="State"
												value={formData.state}
												onChange={handleChange}
												className="checkout-input"
											/>
											<input
												type="text"
												name="zip"
												placeholder="ZIP code"
												value={formData.zip}
												onChange={handleChange}
												className="checkout-input"
												required
											/>
										</div>
									</div>
								)}

								{formData.deliveryMethod === "pickup" && (
									<div className="checkout-section">
										<h3 className="checkout-section-title">Your Information</h3>
										<div className="checkout-row">
											<input
												type="text"
												name="firstName"
												placeholder="First name"
												value={formData.firstName}
												onChange={handleChange}
												className="checkout-input"
												required
											/>
											<input
												type="text"
												name="lastName"
												placeholder="Last name"
												value={formData.lastName}
												onChange={handleChange}
												className="checkout-input"
												required
											/>
										</div>
										<div className="pickup-info-box">
											<p><strong>Pickup Hours</strong></p>
											<p>Mon - Fri: 10AM - 6PM</p>
											<p>Sat: 10AM - 2PM</p>
										</div>
									</div>
								)}

								<button type="submit" className="checkout-continue-btn">
									Continue to Payment
								</button>
							</form>
						) : (
							<div className="checkout-payment">
								<div className="checkout-section">
									<h3 className="checkout-section-title">Review & Pay</h3>
									<div className="checkout-review-box">
										<div className="checkout-review-row">
											<span className="checkout-review-label">Contact</span>
											<span className="checkout-review-value">{formData.email}</span>
										</div>
										<div className="checkout-review-row">
											<span className="checkout-review-label">Name</span>
											<span className="checkout-review-value">{formData.firstName} {formData.lastName}</span>
										</div>
										<div className="checkout-review-row">
											<span className="checkout-review-label">Method</span>
											<span className="checkout-review-value">
												{formData.deliveryMethod === "pickup"
													? "Studio Pickup"
													: DELIVERY_OPTIONS.find((o) => o.id === formData.deliveryZone)?.label || "Delivery"}
											</span>
										</div>
										{formData.deliveryMethod === "delivery" && (
											<div className="checkout-review-row">
												<span className="checkout-review-label">Address</span>
												<span className="checkout-review-value">
													{formData.address}, {formData.city}, {formData.state} {formData.zip}
												</span>
											</div>
										)}
									</div>
									<p className="checkout-stripe-notice">
										You will be redirected to Stripe's secure payment page to complete your order.
									</p>
								</div>

								<div className="checkout-actions">
									<button
										className="checkout-back-btn"
										onClick={() => setStep("information")}
										disabled={isProcessing}
									>
										Back
									</button>
									<button
										className="checkout-pay-btn"
										onClick={handlePlaceOrder}
										disabled={isProcessing}
									>
										{isProcessing ? (
											<span className="checkout-spinner" />
										) : (
											`Pay $${(formData.deliveryMethod === "pickup" ? subtotal : total).toFixed(2)}`
										)}
									</button>
								</div>
							</div>
						)}
					</motion.div>

					<div className="checkout-summary-side">
						<div className="checkout-summary">
							{items.map((item) => (
								<div key={item.id} className="checkout-summary-item">
									<div className="checkout-summary-img-wrap">
										<img src={item.images[0]} alt={item.title} />
										<span className="checkout-summary-qty">{item.quantity}</span>
									</div>
									<div className="checkout-summary-info">
										<p className="checkout-summary-name">{item.title}</p>
									</div>
									<p className="checkout-summary-price">${(item.price * item.quantity).toFixed(2)}</p>
								</div>
							))}

							<div className="checkout-summary-totals">
								<div className="checkout-summary-row">
									<span>Subtotal</span>
									<span>${subtotal.toFixed(2)}</span>
								</div>
								<div className="checkout-summary-row">
									<span>Shipping</span>
									<span>
										{formData.deliveryMethod === "pickup"
											? "Free"
											: `$${shippingCost.toFixed(2)}`}
									</span>
								</div>
								<div className="checkout-summary-row checkout-total-row">
									<span>Total</span>
									<span>${(formData.deliveryMethod === "pickup" ? subtotal : total).toFixed(2)}</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Checkout;
